import { useEffect, useRef } from 'react';
import { useDispatch } from 'react-redux';
import { openModal } from '../Store/modalSlice';
import { getSavedUrlParams, clearSavedUrlParams } from '../utils/urlParams';
import { useTenantData } from './useTenantData';
import { useCanManageAccount } from './usePermissions';
import { useAccountStatus } from './useAccountStatus';

/**
 * Hook to compare URL parameter tier with user's current subscription
 * and show a modal if they're different
 */
export const useSubscriptionComparison = () => {
  const dispatch = useDispatch();
  const { tenantData, isAuthenticated } = useTenantData();
  const { canManageAccount } = useCanManageAccount();
  const { uploadInitialAmount, analyzeInitialAmount, storageQuota, isLoading: accountStatusLoading } = useAccountStatus();
  const hasShownComparison = useRef(false);

  // Helper function to check if free tier user has proper quotas assigned
  const hasFreeTierQuotasAssigned = () => {
    if (!tenantData?.credits) {
      return false;
    }
    
    // For free tier, check if they have the expected quotas:
    // - Upload quota: should be > 0 (free tier gets 10 uploads)
    // - Analyze quota: should be > 0 (free tier gets 3 analyses)  
    const uploadQuota = tenantData.credits.upload?.[0]?.initial_amount || 0;
    const analyzeQuota = tenantData.credits.analyze?.[0]?.initial_amount || 0;
    
    return uploadQuota > 0 && analyzeQuota > 0;
  };

  // Helper function to check if user has any credits assigned at all
  const hasAnyCreditsAssigned = () => {
    if (!tenantData?.credits) {
      return false;
    }
    
    const uploadQuota = tenantData.credits.upload?.[0]?.initial_amount || 0;
    const analyzeQuota = tenantData.credits.analyze?.[0]?.initial_amount || 0;
    
    return uploadQuota > 0 || analyzeQuota > 0;
  };

  useEffect(() => {
    // Only run comparison if user is authenticated and we have tenant data
    if (!isAuthenticated || !tenantData || !tenantData.subscription) {
      return;
    }

    // Only show checkout modal to users who can manage account (Administrator role)
    if (!canManageAccount) {
      console.log('🚫 Checkout modal blocked: User does not have account management permissions');
      return;
    }

    // No need to wait for account status since we're using tenant data directly

    // Prevent showing the modal multiple times
    if (hasShownComparison.current) {
      return;
    }

    // Check if this is a successful checkout return - if so, don't show modal
    const urlParams = new URLSearchParams(window.location.search);
    const isCheckoutSuccess = urlParams.get('checkout_success') === 'true';
    
    // Check if user has no credits assigned at all - force checkout (highest priority)
    if (!hasAnyCreditsAssigned()) {
      console.log('🔍 No credits assigned - forcing checkout');
      
      // Get saved URL parameters to see if a tier was selected
      const savedParams = getSavedUrlParams();
      
      let tierToUse = 'free'; // Default to free tier
      let isYearly = false;
      
      if (savedParams.tier) {
        tierToUse = savedParams.tier;
        isYearly = savedParams.yearly || false;
      }
      
      const currentSubscription = tenantData.subscription.toLowerCase();
      const newTier = tierToUse.toLowerCase();
      
      console.log('🔍 Forced checkout details:', {
        current: currentSubscription,
        new: newTier,
        yearly: isYearly,
        reason: savedParams.tier ? 'forced_checkout_selected_tier' : 'forced_checkout_free_tier'
      });

      // Mark that we've shown the comparison
      hasShownComparison.current = true;

      // Show forced checkout modal
      dispatch(openModal({
        type: 'subscription',
        width: 'lg',
        dismissible: false, // Non-dismissible for forced checkout
        props: {
          currentSubscription: currentSubscription,
          newTier: newTier,
          isYearly: isYearly,
          isForcedCheckout: true
        }
      }));
      return;
    }

    // Get saved URL parameters for normal flow
    const savedParams = getSavedUrlParams();
    
    if (isCheckoutSuccess) {
      console.log('✅ Checkout success detected - skipping subscription modal');
      // Clear the URL parameters to clean up the URL
      const newUrl = new URL(window.location.href);
      newUrl.searchParams.delete('checkout_success');
      newUrl.searchParams.delete('tier');
      newUrl.searchParams.delete('yearly');
      newUrl.searchParams.delete('t');
      window.history.replaceState({}, '', newUrl.toString());
      
      // Clear saved checkout info from localStorage
      clearSavedUrlParams();
      console.log('🧹 Cleared saved checkout parameters from localStorage');
      return;
    }
    
    // If no tier parameter was saved, no comparison needed
    if (!savedParams.tier) {
      return;
    }

    const currentSubscription = tenantData.subscription.toLowerCase();
    const newTier = savedParams.tier.toLowerCase();
    const isYearly = savedParams.yearly || false;

    // Check if we should show the modal
    let shouldShowModal = false;
    let reason = '';

    // Case 1: Different subscription tier
    if (currentSubscription !== newTier) {
      shouldShowModal = true;
      reason = 'subscription_change';
    }
    // Case 2: Free tier user without proper quotas assigned
    else if (currentSubscription === 'free' && newTier === 'free' && !hasFreeTierQuotasAssigned()) {
      shouldShowModal = true;
      reason = 'free_tier_quota_setup';
    }

    if (shouldShowModal) {
      console.log('🔍 Subscription comparison detected:', {
        current: currentSubscription,
        new: newTier,
        yearly: isYearly,
        reason: reason,
        quotas: {
          upload: uploadInitialAmount,
          analyze: analyzeInitialAmount,
          storage: storageQuota
        }
      });

      // Mark that we've shown the comparison
      hasShownComparison.current = true;

      // Show subscription comparison modal
      dispatch(openModal({
        type: 'subscription',
        width: 'lg',
        dismissible: true, // Dismissible for normal flow
        props: {
          currentSubscription: currentSubscription,
          newTier: newTier,
          isYearly: isYearly,
          isForcedCheckout: false
        }
      }));
    } else {
      console.log('✅ No subscription modal needed:', {
        current: currentSubscription,
        new: newTier,
        hasQuotas: hasFreeTierQuotasAssigned()
      });
    }
  }, [isAuthenticated, tenantData, canManageAccount, accountStatusLoading, uploadInitialAmount, analyzeInitialAmount, storageQuota, dispatch]);
}; 